/**
 * ErrorBoundary Component
 *
 * Catches React errors and displays fallback UI
 */

import { Component, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex-1 flex flex-col items-center justify-center p-8 bg-white">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            문제가 발생했습니다
          </h2>
          <p className="text-sm text-gray-600 text-center mb-6 max-w-md">
            {this.state.error?.message || '알 수 없는 오류가 발생했습니다'}
          </p>
          <button
            type="button"
            onClick={this.handleReset}
            className="
              px-4 py-2 rounded-lg
              bg-primary-500 text-white font-medium
              hover:bg-primary-600 active:bg-primary-700
              transition-colors
            "
          >
            다시 시도
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
